import { useState, useEffect } from 'react';
import { api } from '../../../services/api';
import { formatPrice } from '../../../utils/format';
import PaiementModal from '../../../components/feature/PaiementModal';
import { Toast } from '../../../components/base/Toast';
import VenteEditModal from '../../vente-tissu/components/VenteEditModal';
import { checkPermission, PERMISSIONS } from '../../../utils/permissions';

interface VentesTabProps {
  activeBoutique?: 'toutes' | 1 | 2;
}

export default function VentesTab({ activeBoutique = 'toutes' }: VentesTabProps) {
  const [ventesTissu, setVentesTissu] = useState<any[]>([]);
  const [commandes, setCommandes] = useState<any[]>([]);
  const [globalStats, setGlobalStats] = useState<any>(null);
  const [financeStats, setFinanceStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedVenteForPayment, setSelectedVenteForPayment] = useState<any | null>(null);
  const [selectedVenteForEdit, setSelectedVenteForEdit] = useState<any | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [selectedVenteIds, setSelectedVenteIds] = useState<number[]>([]);

  const fetchStats = async () => {
      const token = localStorage.getItem('atelier_bassoum_token');
      if (!token) return;
      try {
        const [vtArr, cmdArr, stats, fStats] = await Promise.all([
          api.getTodayVentes(token, activeBoutique === 'toutes' ? undefined : activeBoutique),
          api.getCommandes(token),
          api.getGlobalStats(token, activeBoutique === 'toutes' ? undefined : activeBoutique),
          api.getFinanceStats(token, activeBoutique === 'toutes' ? undefined : activeBoutique)
        ]);
        setVentesTissu(Array.isArray(vtArr) ? vtArr : []);
        setCommandes(Array.isArray(cmdArr) ? cmdArr : []);
        setGlobalStats(stats);
        setFinanceStats(fStats);
      } catch (err) {
        console.error("Erreur chargement ventes:", err);
      }
  };

  useEffect(() => {
    setSelectedVenteIds([]);
    setLoading(true);
    fetchStats().finally(() => setLoading(false));
  }, [activeBoutique]);

  const handleSolder = async (venteId: number, montantVerse: number, modePaiement: string) => {
    try {
        const token = localStorage.getItem('atelier_bassoum_token') || '';
        await api.updateVentePaiement(token, venteId, { montant_verse: montantVerse, mode_paiement: modePaiement });
        setToast({ message: "Paiement partiel enregistré !", type: "success" });
        setTimeout(() => setToast(null), 3000);
        setSelectedVenteForPayment(null);
        await fetchStats();
    } catch (err: any) {
        setToast({ message: `Erreur: ${err.message || "Inconnue"}`, type: "error" });
        setTimeout(() => setToast(null), 3000);
    }
  };

  const userRole = (() => {
    try {
      return JSON.parse(localStorage.getItem('atelier_bassoum_user') || '{}').role;
    } catch {
      return null;
    }
  })();

  const filteredCommandes = activeBoutique === 'toutes'
    ? commandes
    : commandes.filter(c => Number(c.boutique_id) === Number(activeBoutique));

  const filteredVentesTissu = activeBoutique === 'toutes'
    ? ventesTissu
    : ventesTissu.filter(v => Number(v.boutique_id) === activeBoutique);

  // Aujourd'hui
  const totalTissuToday = filteredVentesTissu.reduce((s, v) => s + Number(v.total), 0);
  const todayStr = new Date().toISOString().split('T')[0];
  const acomptesAujourdhui = filteredCommandes.filter(c => c.date_commande?.startsWith(todayStr));
  const totalToday = financeStats?.jour_encaisse || 0;

  const handleCancelVente = async (id: number) => {
    if (!window.confirm("Êtes-vous sûr de vouloir annuler cette vente ? Le tissu sera remis en stock.")) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      const res = await api.cancelVente(token, id);
      if (res.status === 'success') {
        setVentesTissu(prev => prev.filter(v => v.id !== id));
        // Rafraîchir les stats globales
        const stats = await api.getGlobalStats(token);
        setGlobalStats(stats);
      }
    } catch (err) {
      console.error("Erreur annulation vente:", err);
      alert("Erreur lors de l'annulation.");
    }
  };

  const canCancelVente = ['admin', 'gerant', 'super admin', 'superadmin'].includes(userRole?.toLowerCase() || '') || checkPermission(PERMISSIONS.CANCEL_VENTE);

  const handleBulkCancelVentes = async () => {
    if (selectedVenteIds.length === 0) return;
    if (!window.confirm(`Annuler les ${selectedVenteIds.length} ventes sélectionnées ? Les tissus seront remis en stock.`)) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      setLoading(true);
      for (const id of selectedVenteIds) {
        try {
          await api.cancelVente(token, id);
        } catch (err) {
          console.error(`Erreur d'annulation pour la vente ID ${id}:`, err);
        }
      }
      setSelectedVenteIds([]);
      setToast({ message: "Ventes sélectionnées annulées avec succès !", type: "success" });
      setTimeout(() => setToast(null), 3000);
      await fetchStats();
    } catch (err: any) {
      setToast({ message: `Erreur: ${err.message || "Inconnue"}`, type: "error" });
      setTimeout(() => setToast(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foret-600"></div>
    </div>
  );

  return (
    <div className="space-y-6">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      {/* Résumé financier global & du jour */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-foret-600 text-white rounded-2xl p-5 shadow-xl shadow-foret-100 flex flex-col justify-between">
          <div>
            <p className="text-foret-100 text-[10px] font-bold uppercase tracking-widest mb-1">Encaissement (Total)</p>
            <h2 className="text-2xl font-black">{formatPrice(globalStats?.total_encaisse || 0)}</h2>
          </div>
          <div>
            <div className="mt-4 flex items-center justify-between text-[10px] font-bold text-foret-200 border-t border-foret-500 pt-3">
               <span>AUJOURD&apos;HUI :</span>
               <span className="text-white bg-white/10 px-2 py-0.5 rounded">{formatPrice(totalToday)}</span>
            </div>
            {activeBoutique === 'toutes' && (
              <div className="mt-3 pt-2 border-t border-white/20 flex flex-wrap items-center justify-between text-[10px] text-foret-200 gap-2">

                <div className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-white/60" />
                  <span>B1: <strong className="font-bold text-white">{formatPrice(globalStats?.encaisse_b1 || 0)}</strong></span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-white" />
                  <span>B2: <strong className="font-bold text-white">{formatPrice(globalStats?.encaisse_b2 || 0)}</strong></span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm hover:border-red-100 transition-colors flex flex-col justify-between">
          <div>
            <p className="text-gray-400 text-[10px] font-bold uppercase tracking-widest mb-1">Impayés (Reste à recouvrer)</p>
            <h2 className="text-2xl font-black text-red-600">{formatPrice(globalStats?.total_dette || 0)}</h2>
            <div className="mt-3 flex items-center gap-2 text-[10px] font-black py-1 px-2 bg-red-50 text-red-500 rounded-lg w-fit">
              <i className="ri-error-warning-line" />
              <span>{globalStats?.total_impayes_count || 0} DOSSIERS EN ATTENTE</span>
            </div>
          </div>
          {activeBoutique === 'toutes' && (
            <div className="mt-3 pt-2 border-t border-gray-100 flex flex-wrap items-center justify-between text-[10px] text-gray-400 gap-2">
              <div className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                <span>Couture: <strong className="font-bold text-gray-600">{formatPrice(globalStats?.dette_couture || 0)} ({globalStats?.impayes_count_couture || 0})</strong></span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-terre-400" />
                <span>B1: <strong className="font-bold text-gray-600">{formatPrice(globalStats?.dette_b1 || 0)} ({globalStats?.impayes_count_b1 || 0})</strong></span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-foret-400" />
                <span>B2: <strong className="font-bold text-gray-600">{formatPrice(globalStats?.dette_b2 || 0)} ({globalStats?.impayes_count_b2 || 0})</strong></span>
              </div>
            </div>
          )}
        </div>
        
        <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm flex flex-col justify-between">
          <div>
            <p className="text-gray-400 text-[10px] font-bold uppercase tracking-widest mb-1">Ventes Tissu (Jour)</p>
            <h2 className="text-2xl font-black text-gray-900">{formatPrice(totalTissuToday)}</h2>
            <p className="text-[10px] text-gray-400 font-bold mt-2 uppercase">{filteredVentesTissu.length} transaction(s)</p>
          </div>
          {activeBoutique === 'toutes' && (
            <div className="mt-3 pt-2 border-t border-gray-100 flex items-center justify-between text-[10px] text-gray-400">
              <div className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-terre-400" />
                <span>B1: <strong className="font-bold text-gray-600">{formatPrice(ventesTissu.filter(v => Number(v.boutique_id) === 1).reduce((s, v) => s + Number(v.total), 0))}</strong></span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-foret-400" />
                <span>B2: <strong className="font-bold text-gray-600">{formatPrice(ventesTissu.filter(v => Number(v.boutique_id) === 2).reduce((s, v) => s + Number(v.total), 0))}</strong></span>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm flex flex-col justify-between">
          <div>
            <p className="text-gray-400 text-[10px] font-bold uppercase tracking-widest mb-1">Commandes (Total)</p>
            <h2 className="text-2xl font-black text-gray-900">{globalStats?.commandes_count || 0}</h2>
            <div className="flex items-center gap-2 mt-2">
               <span className="w-2 h-2 rounded-full bg-blue-500" />
               <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tighter">Volume total d&apos;activité</p>
            </div>
          </div>
          {activeBoutique === 'toutes' && (
            <div className="mt-3 pt-2 border-t border-gray-100 flex flex-wrap items-center justify-between text-[10px] text-gray-400 gap-2">
              <div className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                <span>Couture: <strong className="font-bold text-gray-600">{globalStats?.commandes_count_couture || 0}</strong></span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-terre-400" />
                <span>B1: <strong className="font-bold text-gray-600">{globalStats?.commandes_count_b1 || 0}</strong></span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-foret-400" />
                <span>B2: <strong className="font-bold text-gray-600">{globalStats?.commandes_count_b2 || 0}</strong></span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Dernières ventes de tissu */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <h3 className="font-playfair font-bold text-gray-900">Ventes tissus du jour</h3>
              <i className="ri-shopping-bag-3-line text-gray-300" />
            </div>
            {canCancelVente && filteredVentesTissu.length > 0 && (
              <div className="flex items-center justify-between bg-gray-50 p-2 rounded-xl border border-gray-100">
                <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filteredVentesTissu.length > 0 && selectedVenteIds.length === filteredVentesTissu.length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedVenteIds(filteredVentesTissu.map(v => v.id));
                      } else {
                        setSelectedVenteIds([]);
                      }
                    }}
                    className="rounded border-gray-300 text-foret-600 focus:ring-foret-500 w-4 h-4 cursor-pointer"
                  />
                  Tout sélectionner
                </label>
                {selectedVenteIds.length > 0 && (
                  <button
                    onClick={handleBulkCancelVentes}
                    className="flex items-center gap-1 bg-red-50 text-red-600 hover:bg-red-500 hover:text-white px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer border border-red-100"
                  >
                    <i className="ri-delete-bin-line" /> Annuler la sélection ({selectedVenteIds.length})
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="divide-y divide-gray-50 max-h-[400px] overflow-y-auto">
            {filteredVentesTissu.length === 0 ? (
              <div className="text-center py-10 text-gray-400 text-sm">Aucune vente de tissu aujourd&apos;hui</div>
            ) : filteredVentesTissu.map(v => (
              <div key={v.id} className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3">
                  {canCancelVente && (
                    <div className="flex items-center gap-1.5 mr-1" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedVenteIds.includes(v.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedVenteIds(prev => [...prev, v.id]);
                          } else {
                            setSelectedVenteIds(prev => prev.filter(id => id !== v.id));
                          }
                        }}
                        className="rounded border-gray-300 text-foret-600 focus:ring-foret-500 w-4 h-4 cursor-pointer"
                      />
                      <button 
                        onClick={() => handleCancelVente(v.id)}
                        className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all cursor-pointer"
                        title="Annuler cette vente"
                      >
                        <i className="ri-delete-bin-line text-sm" />
                      </button>
                      <button 
                        onClick={() => setSelectedVenteForEdit(v)}
                        className="p-1.5 text-gray-300 hover:text-foret-600 hover:bg-foret-50 rounded-lg transition-all cursor-pointer"
                        title="Modifier cette vente"
                      >
                        <i className="ri-edit-line text-sm" />
                      </button>
                    </div>
                  )}
                  <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center text-foret-600">
                    <i className="ri-scissors-cut-line text-lg" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">{v.tissu_nom}</p>
                    <p className="text-xs text-gray-500">{v.quantite} {v.tissu_unite || 'm'} · {v.client_nom || 'Client anonyme'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right flex flex-col items-end">
                    <p className="text-sm font-black text-gray-900 tracking-tighter">{formatPrice(v.total)}</p>
                    {v.reste > 0 ? (
                      <button 
                        onClick={() => setSelectedVenteForPayment(v)}
                        className="mt-1 text-[9px] bg-red-50 text-red-600 font-bold px-2 py-0.5 rounded-full border border-red-100 hover:bg-red-500 hover:text-white transition-all cursor-pointer"
                      >
                        Reste : {formatPrice(v.reste)}
                      </button>
                    ) : (
                      <span className="text-[10px] text-green-500 font-bold flex items-center gap-1">
                        <i className="ri-checkbox-circle-fill" /> Payé
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Derniers acomptes (commandes) */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-playfair font-bold text-gray-900">Nouveaux acomptes</h3>
            <i className="ri-hand-coin-line text-gray-300" />
          </div>
          <div className="divide-y divide-gray-50 max-h-[400px] overflow-y-auto">
            {acomptesAujourdhui.length === 0 ? (
              <div className="text-center py-10 text-gray-400 text-sm">Aucun acompte encaissé aujourd&apos;hui</div>
            ) : acomptesAujourdhui.map(c => (
              <div key={c.id} className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center text-terre-600">
                    <i className="ri-folder-add-line text-lg" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">CMD #{c.code}</p>
                    <p className="text-xs text-gray-500">{c.client_nom}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-black text-foret-700">{formatPrice(c.acompte || 0, false)}</p>
                  <p className="text-[10px] text-gray-400 uppercase font-bold">Acompte</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {selectedVenteForPayment && (
        <PaiementModal
          reste={Number(selectedVenteForPayment.reste)}
          onValider={(m, p) => handleSolder(selectedVenteForPayment.id, m, p)}
          onClose={() => setSelectedVenteForPayment(null)}
        />
      )}

      {selectedVenteForEdit && (
        <VenteEditModal
          isOpen={true}
          vente={selectedVenteForEdit}
          onClose={() => setSelectedVenteForEdit(null)}
          onSaved={() => {
            setToast({ message: 'Vente modifiée avec succès !', type: 'success' });
            setTimeout(() => setToast(null), 3000);
            setSelectedVenteForEdit(null);
            fetchStats();
          }}
        />
      )}
    </div>
  );
}
